import React, { useState } from 'react';
import { useIntl } from 'react-intl';
import styled from 'styled-components';

import { Flex, Icons, Text, Theme } from 'baukasten';

import messages from './messages';
import type { ThreadMeta } from '../types';

const Row = styled(Flex)<{ $active: boolean }>`
  align-items: center;
  border-radius: 8px;
  background-color: ${({ $active }) =>
    $active ? Theme.getColor('grey-light-50') : 'transparent'};

  &:hover {
    background-color: ${Theme.getColor('grey-light-50')};
  }
`;

const OpenButton = styled.button`
  flex: 1;
  min-width: 0;
  border: none;
  background: transparent;
  padding: ${Theme.getBaseUnit(1)} ${Theme.getBaseUnit(2)};
  text-align: left;
  cursor: pointer;
`;

const Title = styled(Text)`
  display: block;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const ActionButton = styled.button`
  display: flex;
  align-items: center;
  border: none;
  background: transparent;
  padding: ${Theme.getBaseUnit(1)};
  cursor: pointer;
`;

const NameInput = styled.input`
  width: 100%;
  border: 1px solid ${Theme.getColor('lilac')};
  border-radius: 8px;
  outline: none;
  padding: ${Theme.getBaseUnit(1)} ${Theme.getBaseUnit(2)};
  font-family: inherit;
  font-size: ${Theme.getFontSize(14)};
  color: ${Theme.getColor('navy')};
`;

type Props = {
  thread: Pick<ThreadMeta, 'id' | 'name'>;
  active: boolean;
  onOpen: () => void;
  onRename: (name: string) => void;
  onDelete: () => void;
};

export const ThreadRow = ({
  thread,
  active,
  onOpen,
  onRename,
  onDelete,
}: Props) => {
  const { formatMessage } = useIntl();
  const [draft, setDraft] = useState<string | null>(null);

  const commitRename = () => {
    const name = draft?.trim();
    setDraft(null);
    if (name && name !== thread.name) onRename(name);
  };

  if (draft !== null) {
    return (
      <NameInput
        autoFocus
        value={draft}
        aria-label={formatMessage(messages.threadRename)}
        onChange={event => setDraft(event.target.value)}
        onBlur={commitRename}
        onKeyDown={event => {
          if (event.key === 'Enter') commitRename();
          if (event.key === 'Escape') setDraft(null);
        }}
      />
    );
  }

  return (
    <Row $active={active}>
      <OpenButton type="button" onClick={onOpen}>
        <Title fontSize={14} fontWeight={active ? 'bold' : 'regular'}>
          {thread.name || formatMessage(messages.threadUntitled)}
        </Title>
      </OpenButton>
      <Flex alignItems="center">
        <ActionButton
          type="button"
          aria-label={formatMessage(messages.threadRename)}
          onClick={() => setDraft(thread.name ?? '')}
        >
          <Icons.Edit color="grey-dark" size="14px" noPadding />
        </ActionButton>
        <ActionButton
          type="button"
          aria-label={formatMessage(messages.threadDelete)}
          onClick={onDelete}
        >
          <Icons.Delete color="grey-dark" size="14px" noPadding />
        </ActionButton>
      </Flex>
    </Row>
  );
};
